import Helper from './Helper';
import ATime from './ATime';

class MLeg {
  constructor(
    tripLeg,
    legsLength,
    trip,
  ) {
    // variables
    this.id = `${trip['trias:TripId']}_leg-${tripLeg['trias:LegId']}`;

    // elements
    this.element = Object.assign(document.createElement('div'), {
      className: 'm-leg',
      id: this.id,
    });
    this.legsSummaryElement = null;
    this.detailsElement = null;

    // variables
    this.tripLeg = tripLeg;
    this.legsLength = legsLength;
    this.trip = trip;
    this.legId = tripLeg['trias:LegId'];
    this.nth = parseInt(tripLeg['trias:LegId'], 10) - 1;
    this.markers = [];
    this.coordinates = [];
    this.color = null;

    // functions
    this.dateToLocaleTimeString = (date) => (new Date(date)).toLocaleString('de-DE', {
      timeStyle: 'short',
    });

    // event listener
    this.element.addEventListener('click', () => {
      window.mMap?.fitCoordinates(this.coordinates);
    });

    // init
    if (this.leg['trias:NavigationPath']) {
      this.coordinates = Helper.triasNavigationPathToCoordinates(this.leg['trias:NavigationPath']);
    } else if (this.leg['trias:LegTrack']) {
      this.coordinates = Helper.triasLegTrackToCoordinates(this.leg['trias:LegTrack']);
    }
    this.createSummaryElement(tripLeg);
    this.createElement(tripLeg);
  }

  get leg() {
    const { tripLeg } = this;
    return tripLeg['trias:InterchangeLeg'] ?? tripLeg['trias:TimedLeg'] ?? tripLeg['trias:ContinuousLeg'];
  }

  get startName() {
    if (this.type === 'InterchangeLeg' || this.type === 'ContinuousLeg') {
      return this.leg['trias:LegStart']?.['trias:LocationName']?.['trias:Text'];
    }
    if (this.type === 'TimedLeg') {
      return this.leg['trias:LegBoard']?.['trias:StopPointName']?.['trias:Text'];
    }
    return null;
  }

  get endName() {
    if (this.type === 'InterchangeLeg' || this.type === 'ContinuousLeg') {
      return this.leg['trias:LegEnd']?.['trias:LocationName']?.['trias:Text'];
    }
    if (this.type === 'TimedLeg') {
      return this.leg['trias:LegAlight']?.['trias:StopPointName']?.['trias:Text'];
    }
    return null;
  }

  get startTime() {
    const serviceDeparture = this.leg['trias:LegBoard']?.['trias:ServiceDeparture'];
    if (this.type === 'InterchangeLeg' || this.type === 'ContinuousLeg') {
      return this.leg['trias:TimeWindowStart'];
    }
    if (this.type === 'TimedLeg') {
      return serviceDeparture?.['trias:TimetabledTime'];
    }
    return null;
  }

  get endTime() {
    const serviceArrival = this.leg['trias:LegAlight']?.['trias:ServiceArrival'];
    if (this.type === 'InterchangeLeg' || this.type === 'ContinuousLeg') {
      return this.leg['trias:TimeWindowEnd'];
    }
    if (this.type === 'TimedLeg') {
      return serviceArrival?.['trias:TimetabledTime'];
    }
    return null;
  }

  get walkText() {
    const { leg } = this;
    return `Fußweg ${Helper.escapeHtml(leg['trias:Length'])}&thinsp;m, ${Helper.convertISO8601ToString(leg['trias:WalkDuration'] ?? leg['trias:Duration'])} ${leg['trias:BufferTime'] && leg['trias:BufferTime'] !== 'PT0M' ? `<br>Wartezeit: ${Helper.convertISO8601ToString(leg['trias:BufferTime'])}` : ''}`;
  }

  get bayBoard() {
    let bayBoard = this.leg['trias:LegBoard']?.['trias:PlannedBay']?.['trias:Text'] ?? null;
    if (this.mode === 'rail') {
      bayBoard = bayBoard ? `Gleis ${bayBoard}` : null;
    }
    return bayBoard;
  }

  get bayAlight() {
    let bayAlight = this.leg['trias:LegAlight']?.['trias:PlannedBay']?.['trias:Text'] ?? null;
    if (this.mode === 'rail') {
      bayAlight = bayAlight ? `Gleis ${bayAlight}` : null;
    }
    return bayAlight;
  }

  get type() {
    let type = null;
    if (this.tripLeg['trias:InterchangeLeg']) {
      type = 'InterchangeLeg';
    } else if (this.tripLeg['trias:TimedLeg']) {
      type = 'TimedLeg';
    } else if (this.tripLeg['trias:ContinuousLeg']) {
      type = 'ContinuousLeg';
    }
    return type;
  }

  get icsText() {
    let infoText;
    if (this.type === 'InterchangeLeg' || this.type === 'ContinuousLeg') {
      infoText = this.walkText.replaceAll('&thinsp;', ' ');
    } else if (this.type === 'TimedLeg') {
      infoText = `${this.modeName} Richtung ${this.service['trias:DestinationText']['trias:Text']}`;
    }
    return `ab ${this.dateToLocaleTimeString(this.startTime)} ${this.startName} ${this.bayBoard ? `(${this.bayBoard})` : ''}
      ${infoText}
      an ${this.dateToLocaleTimeString(this.endTime)} ${this.endName} ${this.bayAlight ? `(${this.bayAlight})` : ''}`;
  }

  get intermediates() {
    let intermediates = null;
    if (this.leg['trias:LegIntermediates']) {
      intermediates = Array.isArray(this.leg['trias:LegIntermediates'])
        ? this.leg['trias:LegIntermediates']
        : [this.leg['trias:LegIntermediates']];
    }
    return intermediates;
  }

  get attributes() {
    let attributes = null;
    if (this.service?.['trias:Attribute']) {
      attributes = Array.isArray(this.service?.['trias:Attribute'])
        ? this.service?.['trias:Attribute']
        : [this.service?.['trias:Attribute']];
    }
    return attributes;
  }

  get situations() {
    let situations = null;
    if (this.service?.['trias:SituationFullRef']) {
      situations = Array.isArray(this.service?.['trias:SituationFullRef'])
        ? this.service?.['trias:SituationFullRef']
        : [this.service?.['trias:SituationFullRef']];
      situations = situations.map((situation) => {
        const { mSituations } = window.oTripRequest;
        const situationNumber = situation['siri:SituationNumber'];
        return Object.assign(situation, {
          mSituation: mSituations
            .find((mSituation) => situationNumber === mSituation.situationNumber),
        });
      });
    }
    return situations;
  }

  get service() {
    return this.leg?.['trias:Service'];
  }

  // e.g. rail
  get mode() {
    let mode = null;
    if (this.type === 'InterchangeLeg' || this.type === 'ContinuousLeg') {
      mode = this.service?.['trias:IndividualMode'] ?? this.leg['trias:InterchangeMode'];
    } else if (this.type === 'TimedLeg') {
      mode = this.service?.['trias:ServiceSection']?.['trias:Mode']?.['trias:PtMode'];
    }
    return mode;
  }

  // e.g. S-Bahn
  get modeName() {
    let modeName = null;
    if (this.type === 'InterchangeLeg' || this.type === 'ContinuousLeg') {
      modeName = 'Fußweg';
    } else if (this.type === 'TimedLeg') {
      modeName = this.service?.['trias:ServiceSection']?.['trias:Mode']?.['trias:Name']?.['trias:Text'];
      if (this.mode === 'rail') {
        if (this.submode !== 'suburbanRailway') {
          modeName = 'Zug';
        }
      } else if (this.mode === 'bus' && (modeName === 'Buslinie' || modeName === 'Stadtverkehr Reutlingen')) {
        modeName = 'Bus';
      }
    }
    return modeName;
  }

  // e.g. suburbanRailway
  get submode() {
    let submode = null;
    if (this.mode === 'rail') {
      submode = this.service?.['trias:ServiceSection']?.['trias:Mode']['trias:RailSubmode'];
    } else if (this.mode === 'bus') {
      submode = this.service?.['trias:ServiceSection']?.['trias:Mode']['trias:BusSubmode'];
    }
    return submode;
  }

  get isExpanded() {
    return this.detailsElement?.open ?? false;
  }

  expand() {
    if (this.detailsElement) {
      this.detailsElement.open = true;
    }
  }

  updateColor() {
    let color = '';
    if (this.legsSummaryElement) {
      color = getComputedStyle(this.legsSummaryElement).getPropertyValue('--color-route');
    }
    this.color = color === '' ? 'black' : color;
  }

  showRoute() {
    this.updateColor();
    window.mMap?.addLine(this.id, this.coordinates, this.color, this.mode);

    this.markers.push(window.mMap?.addPoint(
      this.startName,
      this.coordinates.at(0),
      this.color,
    ));
    this.markers.push(window.mMap?.addPoint(
      this.endName,
      this.coordinates.at(-1),
      this.color,
    ));
  }

  removeRoute() {
    const { mMap } = window;

    if (mMap) {
      mMap.removeRoute(this.id);
      mMap.removePoint(this.startName, this.coordinates.at(0));
      mMap.removePoint(this.endName, this.coordinates.at(-1));
    }
  }

  getDetailsElement() {
    const { intermediates, attributes, situations } = this;

    const duration = Helper.convertISO8601ToString(this.leg['trias:LegTrack']?.['trias:TrackSection']?.['trias:Duration'] ?? null);

    if (!intermediates) {
      if (!duration) {
        return null;
      }
      const element = document.createElement('div');
      element.classList.add('m-leg__details');
      const intermediatesSpanElement = document.createElement('span');
      intermediatesSpanElement.innerHTML = `Dauer ${duration}`;
      element.appendChild(intermediatesSpanElement);
    }

    const intermediatesSummary = intermediates ? `${intermediates.length}&nbsp;${intermediates.length === 1 ? 'Zwischenhalt' : 'Zwischenhalte'}` : '';
    const durationSummary = intermediates ? duration : `Dauer&nbsp;${duration}`;
    const situationSummary = situations ? `${situations.length}&nbsp;${situations.length === 1 ? 'Störung' : 'Störungen'}` : '';
    const attributesSummary = attributes ? `${attributes.length}&nbsp;${attributes.length === 1 ? 'Hinweis' : 'Hinweise'}` : '';

    const containerElement = document.createElement('div');
    containerElement.classList.add('m-leg__details-container');

    if (situations) {
      const situationsElement = document.createElement('span');
      situationsElement.classList.add('m-leg__situations');
      situations?.forEach((situation) => {
        situationsElement.appendChild(situation.mSituation.element.cloneNode(true));
      });
      containerElement.appendChild(situationsElement);

      situationsElement.querySelectorAll('details').forEach((detailElement) => {
        detailElement.addEventListener('toggle', () => {
          /* eslint-disable-next-line no-underscore-dangle */
          window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'Details Störung']);
        });
      });
    }

    if (attributes) {
      const attributesElement = document.createElement('span');
      attributesElement.classList.add('m-leg__attributes');
      attributes?.forEach((attribute) => {
        const text = attribute['trias:Text']?.['trias:Text'] ?? '';
        attributesElement.appendChild(Object.assign(document.createElement('p'), {
          innerText: text,
        }));
      });
      containerElement.appendChild(attributesElement);
    }

    if (intermediates) {
      intermediates.forEach((intermediate) => {
        const intermediateElement = document.createElement('span');
        intermediateElement.classList.add('m-leg__intermediate');
        intermediateElement.dataset.stopIdRef = intermediate['trias:StopPointRef'];

        const timeTimetabled = intermediate['trias:ServiceDeparture']?.['trias:TimetabledTime'];
        const timeEstimated = intermediate['trias:ServiceDeparture']?.['trias:EstimatedTime'];
        const time = (new ATime(timeEstimated ?? timeTimetabled, timeTimetabled)).element.outerHTML;
        const name = intermediate['trias:StopPointName']?.['trias:Text'];

        intermediateElement.innerHTML = `
          <span class="m-leg__intermediate-time">
            <span class="a-visually-hidden">Zwischenhalt </span>
            ${time}
          </span>
          <span class="m-leg__intermediate-dot"></span>
          <span class="m-leg__intermediate-name">${Helper.escapeHtml(name)}</span>
        `;
        containerElement.appendChild(intermediateElement);
      });
    }

    const summaryContent = [];
    if (durationSummary) {
      summaryContent.push(durationSummary);
    }
    if (intermediatesSummary) {
      summaryContent.push(intermediatesSummary);
    }
    if (situationSummary) {
      summaryContent.push(`<strong>${situationSummary}</strong>`);
    }
    if (attributesSummary) {
      summaryContent.push(attributesSummary);
    }
    const detailsElement = document.createElement(containerElement.innerHTML === '' ? 'span' : 'details');
    detailsElement.classList.add('m-leg__details');
    if (containerElement.innerHTML === '') {
      detailsElement.appendChild(Object.assign(document.createElement('span'), {
        innerHTML: summaryContent.join(', '),
      }));
    } else {
      const summaryElement = document.createElement('summary');
      summaryElement.innerHTML = summaryContent.join(', ');
      detailsElement.appendChild(summaryElement);
      detailsElement.appendChild(containerElement);
    }
    return detailsElement;
  }

  createSummaryElement(tripLeg) {
    this.legsSummaryElement = Helper.getALeg(tripLeg, this.modeName);
    this.legsSummaryElement.addEventListener('click', () => {
      if (window.mMap) {
        window.mMap.fitCoordinates(this.coordinates);
        window.mMap.userInteracted = true;
      }
    });
  }

  createElement() {
    const {
      element,
      tripLeg,
      leg,
      service,
      startName,
      endName,
      startTime,
      endTime,
      bayBoard,
      bayAlight,
    } = this;
    let startTimeEstimated;
    let endTimeEstimated;
    // let startGeoPosition;
    // let endGeoPosition;
    let infoContent = '';

    this.detailsElement = this.getDetailsElement();

    if (this.type === 'InterchangeLeg' || this.type === 'ContinuousLeg') {
      infoContent = this.walkText;
    } else if (this.type === 'TimedLeg') {
      const serviceDeparture = leg['trias:LegBoard']?.['trias:ServiceDeparture'];
      const serviceArrival = leg['trias:LegAlight']?.['trias:ServiceArrival'];

      startTimeEstimated = serviceDeparture?.['trias:EstimatedTime'] ?? serviceDeparture?.['trias:TimetabledTime'];
      endTimeEstimated = serviceArrival?.['trias:EstimatedTime'] ?? serviceArrival?.['trias:TimetabledTime'];

      infoContent = `
        ${Helper.getALeg(tripLeg, this.modeName, true).outerHTML}
        <strong><abbr title="Richtung" aria-hidden="true">→</abbr><span class="a-visually-hidden">Richtung</span> ${Helper.escapeHtml(service['trias:DestinationText']['trias:Text'])}</strong>
      `;
    }

    if (this.mode) {
      element.classList.add(`-${this.mode}`);
    }
    if (this.submode) {
      element.classList.add(`-${this.submode}`);
    }

    if (this.type === 'InterchangeLeg' || this.type === 'ContinuousLeg') {
      if (this.nth === 0) {
        // is first item
        element.appendChild(Object.assign(document.createElement('div'), {
          className: 'm-leg__station-time -start',
          innerHTML: `
            <span class="a-visually-hidden">Start </span>
            ${(new ATime(startTime)).element.outerHTML}
          `,
        }));
        element.appendChild(Object.assign(document.createElement('div'), {
          className: 'm-leg__station-dot -start',
        }));
        element.appendChild(Object.assign(document.createElement('span'), {
          className: 'm-leg__station-name -start',
          textContent: startName,
        }));
      }
      element.appendChild(Object.assign(document.createElement('div'), {
        className: 'm-leg__line',
      }));
      element.appendChild(Object.assign(document.createElement('div'), {
        className: `m-leg__info ${this.mode === 'walk' ? '-walk' : ''}`,
        innerHTML: infoContent,
      }));

      if (this.nth === this.legsLength - 1) {
        // is last item
        element.appendChild(Object.assign(document.createElement('div'), {
          className: 'm-leg__station-time -end',
          innerHTML: `
            <span class="a-visually-hidden">Ankunft </span>
            ${(new ATime(endTime)).element.outerHTML}
          `,
        }));
        element.appendChild(Object.assign(document.createElement('div'), {
          className: 'm-leg__station-dot -end',
        }));
        element.appendChild(Object.assign(document.createElement('span'), {
          className: 'm-leg__station-name -end',
          textContent: endName,
        }));
      }
    } else if (this.type === 'TimedLeg') {
      element.appendChild(Object.assign(document.createElement('div'), {
        className: 'm-leg__station-time -start',
        innerHTML: `
          <span class="a-visually-hidden">Abfahrt </span>
          ${(new ATime(startTimeEstimated, startTime)).element.outerHTML}
        `,
      }));
      element.appendChild(Object.assign(document.createElement('div'), {
        className: 'm-leg__station-dot -start',
      }));
      element.appendChild(Object.assign(document.createElement('span'), {
        className: 'm-leg__station-name -start',
        innerHTML: `
          ${Helper.escapeHtml(startName)}
          ${bayBoard ? `<small>${Helper.escapeHtml(bayBoard)}</small>` : ''}
        `,
      }));

      element.appendChild(Object.assign(document.createElement('div'), {
        className: 'm-leg__line',
      }));
      element.appendChild(Object.assign(document.createElement('div'), {
        className: 'm-leg__info',
        innerHTML: infoContent,
      }));
      if (this.detailsElement) {
        element.appendChild(this.detailsElement);
      }

      element.appendChild(Object.assign(document.createElement('div'), {
        className: 'm-leg__station-time -end',
        innerHTML: `
          <span class="a-visually-hidden">Ankunft </span>
          ${(new ATime(endTimeEstimated, endTime)).element.outerHTML}
        `,
      }));
      element.appendChild(Object.assign(document.createElement('div'), {
        className: 'm-leg__station-dot -end',
      }));
      element.appendChild(Object.assign(document.createElement('span'), {
        className: 'm-leg__station-name -end',
        innerHTML: `
          ${Helper.escapeHtml(endName)}
          ${bayAlight ? `<small>${Helper.escapeHtml(bayAlight)}</small>` : ''}
        `,
      }));
    }

    if (service?.['trias:ServiceSection']?.['trias:OperatorRef'] === 'tub:07') {
      const lineName = service['trias:ServiceSection']['trias:PublishedLineName']?.['trias:Text'];
      const { color, colorHighContrast, colorContent } = Helper.getBusLineColors(lineName);
      if (color && colorContent && colorHighContrast) {
        element.style = `--color-route: ${color}; --color-route-high-contrast: ${colorHighContrast}; --color-content: ${colorContent}`;
        this.color = colorHighContrast;
      }
    }

    // event listener
    const onStationNameClick = (event) => {
      event.stopPropagation();
      const targetElement = event.target.classList.contains('.m-leg__station-name') ? event.target : event.target.closest('.m-leg__station-name');
      let coordinates = null;
      if (targetElement.classList.contains('-start')) {
        coordinates = this.coordinates.at(0);
      } else if (targetElement.classList.contains('-end')) {
        coordinates = this.coordinates.at(-1);
      }
      if (coordinates) {
        window.mMap?.zoomToStation(coordinates);
      }

      /* eslint-disable-next-line no-underscore-dangle */
      window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'Click', 'Station Name']);
    };

    this.detailsElement?.addEventListener('toggle', () => {
      if (this.detailsElement.open) {
        /* eslint-disable-next-line no-underscore-dangle */
        window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'Details', 'Leg']);
      }
    });
    element.querySelector('.m-leg__station-name.-start')?.addEventListener('click', onStationNameClick);
    element.querySelector('.m-leg__station-dot.-start')?.addEventListener('click', onStationNameClick);
    element.querySelector('.m-leg__station-dot.-end')?.addEventListener('click', onStationNameClick);
    element.querySelector('.m-leg__station-name.-end')?.addEventListener('click', onStationNameClick);
  }
}

export default MLeg;
